// A4 GST Tax Invoice for B2B Sub-Dealer billing.
// Matches the user's TUNI dealership template:
//  - Dealership letterhead (logo + GSTIN + state code + address)
//  - "TAX INVOICE" title + invoice no + date + FY
//  - Bill-To (sub-dealer branch w/ GSTIN, address, state code)
//  - Particulars table: SN, Model, VIN/Chassis, Engine, HSN, Qty, Rate, Taxable, CGST/SGST or IGST, Total
//  - Totals + Tax summary + Amount in Words
//  - Signature blocks
import { fmtDate } from "@/lib/format";

function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

const fmtAmt = (n) => (Number(n) || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function numToIndianWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => {
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    const t = Math.floor(x / 10);
    const o = x % 10;
    return tens[t] + (o ? " " + ones[o] : "");
  };
  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    let s = "";
    if (h) s += ones[h] + " Hundred";
    if (rest) s += (h ? " " : "") + twoDigits(rest);
    return s;
  };
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(twoDigits(lakh) + " Lakh");
  if (thousand) parts.push(twoDigits(thousand) + " Thousand");
  if (hundred) parts.push(threeDigits(hundred));
  return "Rupees " + parts.join(" ").replace(/\s+/g, " ").trim() + " Only";
}

export default function buildSubDealerTaxInvoiceHtml(invoice) {
  const inv = invoice || {};
  const d = inv.dealership || {};
  const b = inv.branch || {};
  const isIntra = inv.gst_type === "intra";
  const lines = inv.lines || [];

  // Aggregate per-line GST for the "Tax Summary" block, grouped by HSN.
  // The TUNI invoice format expands each vehicle into multiple rows (vehicle
  // body + mandatory accessories + helmet/other) — one row per package bucket
  // — each with its own HSN and Amount. The Tax Summary at the bottom rolls
  // these up per HSN.
  const ACCESSORIES_HSN = "87141090"; // common Indian bike-accessories HSN
  const taxByHsn = {};
  const addHsn = (hsn, taxable, cgst, sgst, igst, ratePct) => {
    const key = (hsn || "").trim() || "—";
    if (!taxByHsn[key]) {
      taxByHsn[key] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, rate_pct: ratePct ?? 0 };
    }
    taxByHsn[key].taxable += taxable;
    taxByHsn[key].cgst += cgst;
    taxByHsn[key].sgst += sgst;
    taxByHsn[key].igst += igst;
    if (ratePct !== undefined) taxByHsn[key].rate_pct = ratePct;
  };

  // Build the items table rows + aggregate the HSN-wise tax summary.
  let serial = 0;
  const itemRows = lines.map((ln) => {
    const exTaxable = Number(ln.ex_showroom_taxable) || 0;
    const otherTaxable = Number(ln.other_taxable) || 0;
    const accInclusive = Number(ln.accessories_inclusive) || 0;
    const accTaxable = Number(ln.accessories_taxable) || 0;
    const accGst = Number(ln.accessories_gst) || 0;
    const accGstPct = Number(ln.acc_gst_pct) || 0;
    // Invoice-rate GST applies to (ex + other). Split proportionally so each
    // sub-row's CGST/SGST/IGST attributes back to its own HSN in the Tax
    // Summary block. Fallback: if no bucket data, treat the whole line as
    // one ex_showroom-only row (legacy invoices).
    const hasBuckets = exTaxable > 0 || otherTaxable > 0 || accInclusive > 0;
    const invTaxableTotal = exTaxable + otherTaxable;
    const invCgstTotal = Number(ln.cgst) || 0;
    const invSgstTotal = Number(ln.sgst) || 0;
    const invIgstTotal = Number(ln.igst) || 0;
    // The line's CGST already includes the accessories' half-GST (intra) — we
    // need to subtract that to get the "invoice-rate-only" portion before
    // splitting it proportionally between ex and other.
    const accCgstShare = isIntra ? accGst / 2 : 0;
    const accSgstShare = isIntra ? accGst / 2 : 0;
    const accIgstShare = isIntra ? 0 : accGst;
    const invOnlyCgst = invCgstTotal - accCgstShare;
    const invOnlySgst = invSgstTotal - accSgstShare;
    const invOnlyIgst = invIgstTotal - accIgstShare;
    // Per-line rate stamped by the backend from the AD package master.
    // Falls back to the invoice-level rate for legacy invoices (created
    // before the per-line rate field existed).
    const lineCgstPct = ln.cgst_pct != null ? Number(ln.cgst_pct) : (Number(inv.cgst_pct) || 0);
    const lineIgstPct = ln.igst_pct != null ? Number(ln.igst_pct) : (Number(inv.igst_pct) || 0);
    const rateForHsn = isIntra ? lineCgstPct : lineIgstPct;

    // Row 1 — the vehicle itself (ex-showroom taxable value @ vehicle HSN).
    const rows = [];
    if (!hasBuckets) {
      // Legacy fallback — single row with the whole taxable value.
      serial += 1;
      const taxable = Number(ln.subtotal) || 0;
      addHsn(ln.hsn_code, taxable, invCgstTotal, invSgstTotal, invIgstTotal, rateForHsn);
      rows.push(`<tr>
        <td class="num">${serial}</td>
        <td><b>${escHtml([ln.package_name || ln.model, ln.color].filter(Boolean).join("-"))}</b>${ln.vin ? `<br/><span class="dim">Batch: ${escHtml(ln.vin)}${ln.engine_no ? "/" + escHtml(ln.engine_no) : ""}</span>` : ""}</td>
        <td class="mono">${escHtml(ln.hsn_code || "—")}</td>
        <td class="num">${ln.quantity || 1} NOS</td>
        <td class="num">${escHtml(fmtAmt(taxable))}</td>
        <td class="num">NOS</td>
        <td class="num">${escHtml(fmtAmt(taxable))}</td>
      </tr>`);
      return rows.join("");
    }

    if (exTaxable > 0) {
      serial += 1;
      const exShare = invTaxableTotal > 0 ? exTaxable / invTaxableTotal : 1;
      const exCgst = invOnlyCgst * exShare;
      const exSgst = invOnlySgst * exShare;
      const exIgst = invOnlyIgst * exShare;
      addHsn(ln.hsn_code, exTaxable, exCgst, exSgst, exIgst, rateForHsn);
      rows.push(`<tr>
        <td class="num">${serial}</td>
        <td><b>${escHtml([ln.package_name || ln.model, ln.color].filter(Boolean).join("-"))}</b>${ln.vin ? `<br/><span class="dim">Batch: ${escHtml(ln.vin)}${ln.engine_no ? "/" + escHtml(ln.engine_no) : ""}</span>` : ""}</td>
        <td class="mono">${escHtml(ln.hsn_code || "—")}</td>
        <td class="num">${ln.quantity || 1} NOS</td>
        <td class="num">${escHtml(fmtAmt(exTaxable))}</td>
        <td class="num">NOS</td>
        <td class="num">${escHtml(fmtAmt(exTaxable))}</td>
      </tr>`);
    }
    if (accInclusive > 0) {
      serial += 1;
      addHsn(ACCESSORIES_HSN, accTaxable, isIntra ? accGst / 2 : 0, isIntra ? accGst / 2 : 0, isIntra ? 0 : accGst, accGstPct / (isIntra ? 2 : 1));
      rows.push(`<tr>
        <td class="num">${serial}</td>
        <td>MANDATORY ACCESSORIES</td>
        <td class="mono">${escHtml(ACCESSORIES_HSN)}</td>
        <td class="num">${ln.quantity || 1} NOS</td>
        <td class="num">${escHtml(fmtAmt(accTaxable))}</td>
        <td class="num">NOS</td>
        <td class="num">${escHtml(fmtAmt(accTaxable))}</td>
      </tr>`);
    }
    if (otherTaxable > 0) {
      serial += 1;
      const otherShare = invTaxableTotal > 0 ? otherTaxable / invTaxableTotal : 1;
      const oCgst = invOnlyCgst * otherShare;
      const oSgst = invOnlySgst * otherShare;
      const oIgst = invOnlyIgst * otherShare;
      addHsn(ln.hsn_code, otherTaxable, oCgst, oSgst, oIgst, rateForHsn);
      rows.push(`<tr>
        <td class="num">${serial}</td>
        <td>OTHER CHARGES</td>
        <td class="mono">${escHtml(ln.hsn_code || "—")}</td>
        <td class="num">${ln.quantity || 1} NOS</td>
        <td class="num">${escHtml(fmtAmt(otherTaxable))}</td>
        <td class="num">NOS</td>
        <td class="num">${escHtml(fmtAmt(otherTaxable))}</td>
      </tr>`);
    }
    return rows.join("");
  }).join("");

  const taxRows = Object.entries(taxByHsn).map(([hsn, t]) => `<tr>
    <td class="mono">${escHtml(hsn)}</td>
    <td class="num">${escHtml(fmtAmt(t.taxable))}</td>
    ${isIntra
      ? `<td class="num">${t.rate_pct}%</td>
         <td class="num">${escHtml(fmtAmt(t.cgst))}</td>
         <td class="num">${t.rate_pct}%</td>
         <td class="num">${escHtml(fmtAmt(t.sgst))}</td>`
      : `<td class="num">${t.rate_pct}%</td>
         <td class="num">${escHtml(fmtAmt(t.igst))}</td>`
    }
    <td class="num"><b>${escHtml(fmtAmt(t.cgst + t.sgst + t.igst))}</b></td>
  </tr>`).join("");

  const totalTax = (Number(inv.cgst_total) || 0) + (Number(inv.sgst_total) || 0) + (Number(inv.igst_total) || 0);
  const totalQty = lines.reduce((s, ln) => s + (Number(ln.quantity) || 1), 0);

  // Bucket composition — surfaces the Ex-showroom (no tax) / Other / Accessories
  // (GST-inclusive) split per the package definition. Falls back to "—" rows
  // for legacy invoices created before the bucket split was persisted.
  const hasBucketData = lines.some(
    (ln) =>
      (Number(ln.ex_showroom_taxable) || 0) > 0
      || (Number(ln.accessories_inclusive) || 0) > 0
      || (Number(ln.other_taxable) || 0) > 0,
  );
  const compTotals = lines.reduce(
    (acc, ln) => ({
      ex: acc.ex + (Number(ln.ex_showroom_taxable) || 0),
      other: acc.other + (Number(ln.other_taxable) || 0),
      acc_incl: acc.acc_incl + (Number(ln.accessories_inclusive) || 0),
      acc_tax: acc.acc_tax + (Number(ln.accessories_taxable) || 0),
      acc_gst: acc.acc_gst + (Number(ln.accessories_gst) || 0),
    }),
    { ex: 0, other: 0, acc_incl: 0, acc_tax: 0, acc_gst: 0 },
  );
  const acc_pct = lines.find((ln) => Number(ln.acc_gst_pct) > 0)?.acc_gst_pct || 0;
  const compositionRows = hasBucketData
    ? `<table class="composition">
        <thead>
          <tr>
            <th>Component</th>
            <th class="num">Taxable Value (₹)</th>
            <th class="num">GST (₹)</th>
            <th class="num">Inclusive (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Ex-showroom (no tax in pkg) — taxed at invoice rate</td>
            <td class="num">${escHtml(fmtAmt(compTotals.ex))}</td>
            <td class="num">on invoice</td>
            <td class="num">—</td>
          </tr>
          ${compTotals.other > 0
            ? `<tr>
                <td>Other (insurance / AMC / extended warranty) — taxed at invoice rate</td>
                <td class="num">${escHtml(fmtAmt(compTotals.other))}</td>
                <td class="num">on invoice</td>
                <td class="num">—</td>
              </tr>`
            : ""}
          ${compTotals.acc_incl > 0
            ? `<tr>
                <td>Accessories — GST-inclusive @ ${acc_pct}%</td>
                <td class="num">${escHtml(fmtAmt(compTotals.acc_tax))}</td>
                <td class="num">${escHtml(fmtAmt(compTotals.acc_gst))}</td>
                <td class="num">${escHtml(fmtAmt(compTotals.acc_incl))}</td>
              </tr>`
            : ""}
        </tbody>
      </table>`
    : "";

  return `<!doctype html><html><head><title>Tax Invoice · ${escHtml(inv.invoice_no || "")}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:18px;background:#fff;max-width:820px}
  .hdr{display:flex;align-items:flex-start;gap:18px;border:2px solid #000;padding:10px 14px;margin-bottom:0}
  .logo{max-height:70px;max-width:140px;object-fit:contain}
  .dealer{flex:1;line-height:1.35}
  .dealer .name{font-size:18px;font-weight:700;letter-spacing:.5px}
  .dealer .sub{font-size:11px;color:#222}
  .title-bar{border:2px solid #000;border-top:0;text-align:center;font-size:15px;letter-spacing:6px;font-weight:700;padding:6px;background:#f4f4f4}
  .meta{display:grid;grid-template-columns:1fr 1fr;border:2px solid #000;border-top:0}
  .meta > div{padding:7px 10px;font-size:11.5px;line-height:1.5}
  .meta > div + div{border-left:1px solid #000}
  .meta .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555;display:block}
  .meta .val{font-weight:700}
  .billto{border:2px solid #000;border-top:0;padding:8px 12px;font-size:12px;line-height:1.5}
  .billto .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555;margin-bottom:2px}
  .billto .name{font-size:14px;font-weight:700}
  table.items{width:100%;border-collapse:collapse;border:2px solid #000;border-top:0}
  table.items th,table.items td{border:1px solid #000;padding:5px 6px;font-size:10.5px;vertical-align:middle}
  table.items th{background:#eee;font-family:'IBM Plex Mono',monospace;font-size:9.5px;letter-spacing:.05em;text-transform:uppercase}
  .mono{font-family:'Courier New',monospace;font-size:10.5px}
  td.num,th.num{text-align:right;font-family:'Courier New',monospace}
  .totals-row td{font-weight:700;background:#f4f4f4}
  .summary{display:grid;grid-template-columns:1.4fr 1fr;gap:0;border:2px solid #000;border-top:0}
  .summary .left{padding:8px 12px;font-size:11px;line-height:1.6;border-right:1px solid #000}
  .summary .left .words{font-style:italic;margin-bottom:6px}
  .summary .left .words b{font-style:normal}
  .summary .right table{width:100%;border-collapse:collapse}
  .summary .right td{padding:5px 10px;font-size:11.5px;border-bottom:1px solid #ddd}
  .summary .right td:last-child{text-align:right;font-family:'Courier New',monospace}
  .summary .right tr:last-child td{border-bottom:0;font-weight:700;background:#f4f4f4;font-size:13px}
  .composition{width:100%;border-collapse:collapse;margin-top:6px;font-size:10.5px}
  .composition th,.composition td{border:1px solid #999;padding:4px 6px}
  .composition th{background:#f4f4f4;font-size:9.5px;text-transform:uppercase;letter-spacing:0.05em;color:#555}
  .composition td.num{text-align:right;font-family:'Courier New',monospace}
  table.tax{width:100%;border-collapse:collapse;border:2px solid #000;border-top:0}
  table.tax th,table.tax td{border:1px solid #000;padding:4px 8px;font-size:10.5px}
  table.tax th{background:#eee;font-family:'IBM Plex Mono',monospace;font-size:9.5px;text-transform:uppercase}
  .sig{display:grid;grid-template-columns:1fr 1fr;gap:0;border:2px solid #000;border-top:0;font-size:11px}
  .sig > div{padding:10px 14px 30px;border-right:1px solid #000;min-height:90px}
  .sig > div:last-child{border-right:0;text-align:right}
  .sig .lbl{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#555}
  .sig .for{font-weight:700;margin-top:6px}
  .notes{padding:6px 12px;font-size:10.5px;border:2px solid #000;border-top:0;font-style:italic;color:#333;background:#fafafa}
  .foot{margin-top:18px;font-family:'IBM Plex Mono',monospace;font-size:9px;color:#777;text-align:center;letter-spacing:.18em;text-transform:uppercase}
  @media print{body{margin:8mm} button{display:none}}
</style></head><body>
<div class="hdr">
  ${d.logo_url ? `<img class="logo" src="${escHtml(d.logo_url)}" alt="logo"/>` : ""}
  <div class="dealer">
    <div class="name">${escHtml(d.name || "DEALERSHIP")}</div>
    ${d.address ? `<div class="sub">${escHtml(d.address)}</div>` : ""}
    <div class="sub">
      ${d.gstin ? `GSTIN: <b>${escHtml(d.gstin)}</b>` : ""}
      ${d.state_name ? ` &nbsp;·&nbsp; State: ${escHtml(d.state_name)} (Code ${escHtml(d.state_code || "—")})` : ""}
    </div>
    ${(d.phone || d.email) ? `<div class="sub">${[d.phone, d.email].filter(Boolean).map(escHtml).join(" · ")}</div>` : ""}
  </div>
</div>
<div class="title-bar">TAX INVOICE</div>

<div class="meta">
  <div>
    <span class="lbl">Invoice No</span>
    <span class="val">${escHtml(inv.invoice_no || "—")}</span>
    ${inv.fy_label ? ` &nbsp; <span class="lbl" style="display:inline">FY</span> <b>${escHtml(inv.fy_label)}</b>` : ""}
  </div>
  <div>
    <span class="lbl">Invoice Date</span>
    <span class="val">${escHtml(fmtDate(inv.date))}</span>
  </div>
</div>

<div class="billto">
  <div class="lbl">Bill To · Buyer (Sub-Dealer)</div>
  <div class="name">${escHtml(b.name || "—")}</div>
  ${b.address ? `<div>${escHtml(b.address)}</div>` : ""}
  <div>
    ${b.gstin ? `GSTIN: <b>${escHtml(b.gstin)}</b>` : ""}
    ${b.code ? ` &nbsp;·&nbsp; Branch Code: ${escHtml(b.code)}` : ""}
    ${b.phone ? ` &nbsp;·&nbsp; Ph: ${escHtml(b.phone)}` : ""}
  </div>
</div>

<table class="items">
  <thead>
    <tr>
      <th class="num">Sl No</th>
      <th>Description of Goods</th>
      <th>HSN/SAC</th>
      <th class="num">Quantity</th>
      <th class="num">Rate (₹)</th>
      <th class="num">per</th>
      <th class="num">Amount (₹)</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows || `<tr><td colspan="7" style="text-align:center;color:#888;padding:18px">No line items.</td></tr>`}
    <tr class="totals-row">
      <td colspan="4" style="text-align:right">Sub-total</td>
      <td></td>
      <td></td>
      <td class="num">${escHtml(fmtAmt(inv.subtotal))}</td>
    </tr>
    ${isIntra
      ? `<tr><td colspan="6" style="text-align:right">Add: CGST</td><td class="num">${escHtml(fmtAmt(inv.cgst_total))}</td></tr>
         <tr><td colspan="6" style="text-align:right">Add: SGST</td><td class="num">${escHtml(fmtAmt(inv.sgst_total))}</td></tr>`
      : `<tr><td colspan="6" style="text-align:right">Add: IGST</td><td class="num">${escHtml(fmtAmt(inv.igst_total))}</td></tr>`
    }
    ${Number(inv.company_discount_total) > 0
      ? `<tr><td colspan="6" style="text-align:right">Less: Company Discount / OEM Rebate</td><td class="num">- ${escHtml(fmtAmt(inv.company_discount_total))}</td></tr>`
      : ""
    }
    <tr class="totals-row">
      <td colspan="6" style="text-align:right"><b>Grand Total</b></td>
      <td class="num"><b>${escHtml(fmtAmt(inv.grand_total))}</b></td>
    </tr>
  </tbody>
</table>

<table class="tax">
  <thead>
    <tr>
      <th>HSN</th>
      <th class="num">Taxable Value</th>
      ${isIntra
        ? `<th class="num">CGST %</th><th class="num">CGST ₹</th><th class="num">SGST %</th><th class="num">SGST ₹</th>`
        : `<th class="num">IGST %</th><th class="num">IGST ₹</th>`
      }
      <th class="num">Total (₹)</th>
    </tr>
  </thead>
  <tbody>${taxRows}</tbody>
</table>

<div class="summary">
  <div class="left">
    <div class="words">Amount in Words: <b>${escHtml(numToIndianWords(inv.grand_total))}</b></div>
    <div><b>Bank Details for Payment:</b> ${escHtml(d.bank_details || "—")}</div>
    <div style="margin-top:6px;font-size:10px;color:#444">
      Declaration: We declare that this invoice shows the actual price of the goods
      described and that all particulars are true and correct.
    </div>
    ${compositionRows ? "" : ""}
  </div>
  <div class="right">
    <table>
      <tr><td>Taxable Amount</td><td>${escHtml(fmtAmt(inv.subtotal))}</td></tr>
      ${isIntra
        ? `<tr><td>CGST${Number(inv.cgst_pct) > 0 ? ` @ ${inv.cgst_pct}%` : ""}</td><td>${escHtml(fmtAmt(inv.cgst_total))}</td></tr>
           <tr><td>SGST${Number(inv.sgst_pct) > 0 ? ` @ ${inv.sgst_pct}%` : ""}</td><td>${escHtml(fmtAmt(inv.sgst_total))}</td></tr>`
        : `<tr><td>IGST${Number(inv.igst_pct) > 0 ? ` @ ${inv.igst_pct}%` : ""}</td><td>${escHtml(fmtAmt(inv.igst_total))}</td></tr>`
      }
      <tr><td>Total Tax</td><td>${escHtml(fmtAmt(totalTax))}</td></tr>
      ${Number(inv.company_discount_total) > 0
        ? `<tr><td>Less: Company Discount</td><td>- ${escHtml(fmtAmt(inv.company_discount_total))}</td></tr>`
        : ""
      }
      <tr><td>Grand Total (₹)</td><td>${escHtml(fmtAmt(inv.grand_total))}</td></tr>
    </table>
  </div>
</div>

${inv.notes ? `<div class="notes">Notes: ${escHtml(inv.notes)}</div>` : ""}

<div class="sig">
  <div>
    <div class="lbl">Receiver's Signature</div>
    <div style="margin-top:6px;font-size:10px;color:#555">
      Goods received in good condition.
    </div>
  </div>
  <div>
    <div class="for">For ${escHtml(d.name || "DEALERSHIP")}</div>
    <div class="lbl" style="margin-top:40px">Authorised Signatory</div>
  </div>
</div>

<div class="foot">Computer-generated tax invoice · ${escHtml(new Date().toLocaleString())}</div>
<script>
  window.addEventListener('load', function () {
    var imgs = Array.from(document.images || []);
    var pending = imgs.filter(function (im) { return !im.complete; });
    if (pending.length === 0) { setTimeout(function () { window.print(); }, 200); return; }
    var left = pending.length;
    pending.forEach(function (im) {
      im.addEventListener('load', function () { if (--left === 0) setTimeout(function () { window.print(); }, 150); });
      im.addEventListener('error', function () { if (--left === 0) setTimeout(function () { window.print(); }, 150); });
    });
  });
</script>
</body></html>`;
}
